import { useState, useRef, useCallback } from "react";

/**
 * useVoice — hybrid speech-to-text + text-to-speech.
 *
 * STT strategy:
 *   1. Browser SpeechRecognition (Chrome/Safari) — real-time, zero latency
 *   2. Whisper via Workers AI (Firefox/all browsers) — MediaRecorder + POST /v1/transcribe
 *
 * TTS: Browser SpeechSynthesis for reading responses aloud.
 * Voice mode: auto-read responses + re-activate mic after TTS finishes.
 */

const API_BASE = import.meta.env.VITE_AI_GATEWAY_URL || "";

function getBrowserSTT(): any {
  if (typeof window === "undefined") return null;
  return (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition || null;
}

function getAuthHeaders(): Record<string, string> {
  const h: Record<string, string> = {};
  const token = localStorage.getItem("infra_token");
  if (token) h["authorization"] = `Bearer ${token}`;
  return h;
}

export function useVoice() {
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const recognitionRef = useRef<any>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);

  const hasBrowserSTT = !!getBrowserSTT();
  // MediaRecorder is supported everywhere — Whisper fallback always works
  const supported = true;

  // ── Browser SpeechRecognition (primary) ────────────────────

  const startBrowserSTT = useCallback(() => {
    const STT = getBrowserSTT();
    if (!STT) return false;

    window.speechSynthesis?.cancel();

    const recognition = new STT();
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = "en-US";

    recognition.onresult = (event: any) => {
      let finalTranscript = "";
      let interimTranscript = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          finalTranscript += event.results[i][0].transcript;
        } else {
          interimTranscript += event.results[i][0].transcript;
        }
      }
      setTranscript((finalTranscript || interimTranscript).trim());
    };

    recognition.onend = () => {
      setListening(false);
      recognitionRef.current = null;
    };

    recognition.onerror = () => {
      setListening(false);
      recognitionRef.current = null;
    };

    recognitionRef.current = recognition;
    recognition.start();
    setListening(true);
    setTranscript("");
    return true;
  }, []);

  // ── Whisper via MediaRecorder (fallback) ───────────────────

  const startWhisperSTT = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      window.speechSynthesis?.cancel();

      // Use webm/opus if available, otherwise any supported format
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/webm")
          ? "audio/webm"
          : "audio/mp4";

      const recorder = new MediaRecorder(stream, { mimeType });
      audioChunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      recorder.onstop = async () => {
        // Stop all tracks
        stream.getTracks().forEach((t) => t.stop());

        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        if (audioBlob.size < 100) {
          setListening(false);
          return;
        }

        // Send to Whisper
        setTranscribing(true);
        try {
          const res = await fetch(`${API_BASE}/v1/transcribe`, {
            method: "POST",
            headers: getAuthHeaders(),
            body: audioBlob,
          });
          if (res.ok) {
            const data = await res.json();
            if (data.text?.trim()) {
              setTranscript(data.text.trim());
            }
          }
        } catch (e) {
          console.warn("Whisper transcription failed:", e);
        } finally {
          setTranscribing(false);
          setListening(false);
        }
      };

      mediaRecorderRef.current = recorder;
      recorder.start();
      setListening(true);
      setTranscript("");
      return true;
    } catch (e) {
      console.warn("Microphone access denied:", e);
      return false;
    }
  }, []);

  // ── Public API ─────────────────────────────────────────────

  const startListening = useCallback(() => {
    // Try browser STT first (real-time, better for conversation)
    if (hasBrowserSTT) {
      startBrowserSTT();
      return;
    }
    // Fall back to Whisper
    startWhisperSTT();
  }, [hasBrowserSTT, startBrowserSTT, startWhisperSTT]);

  const stopListening = useCallback(() => {
    // Stop browser STT
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    // Stop MediaRecorder (triggers onstop → Whisper transcription)
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current = null;
      return; // Don't set listening=false yet — onstop will do it after transcription
    }
    setListening(false);
  }, []);

  // ── TTS ────────────────────────────────────────────────────

  const speak = useCallback((text: string) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();

    const cleanText = text
      .replace(/```[\s\S]*?```/g, "code block omitted")
      .replace(/`[^`]+`/g, (m) => m.slice(1, -1))
      .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
      .replace(/https?:\/\/\S+/g, "link")
      .replace(/[#*_~>]/g, "")
      .replace(/\n{2,}/g, ". ")
      .replace(/\n/g, " ")
      .trim();

    if (!cleanText) return;

    const chunks = splitIntoChunks(cleanText, 180);
    setSpeaking(true);

    let i = 0;
    const speakNext = () => {
      if (i >= chunks.length) {
        setSpeaking(false);
        if (voiceEnabled) setTimeout(() => startListening(), 500);
        return;
      }
      const utt = new SpeechSynthesisUtterance(chunks[i]);
      utt.rate = 1.0;
      utt.pitch = 1.0;

      const voices = window.speechSynthesis.getVoices();
      const preferred = voices.find((v) => v.name.includes("Google") && v.lang.startsWith("en"))
        || voices.find((v) => v.lang.startsWith("en") && v.localService)
        || voices.find((v) => v.lang.startsWith("en"));
      if (preferred) utt.voice = preferred;

      utt.onend = () => { i++; speakNext(); };
      utt.onerror = () => setSpeaking(false);
      window.speechSynthesis.speak(utt);
    };
    speakNext();
  }, [voiceEnabled, startListening]);

  const stopSpeaking = useCallback(() => {
    window.speechSynthesis?.cancel();
    setSpeaking(false);
  }, []);

  const toggleVoice = useCallback(() => {
    const next = !voiceEnabled;
    setVoiceEnabled(next);
    if (!next) { stopListening(); stopSpeaking(); }
  }, [voiceEnabled, stopListening, stopSpeaking]);

  const clearTranscript = useCallback(() => setTranscript(""), []);

  return {
    supported,
    listening,
    transcript,
    voiceEnabled,
    speaking,
    transcribing,
    hasBrowserSTT,
    startListening,
    stopListening,
    speak,
    stopSpeaking,
    toggleVoice,
    clearTranscript,
  };
}

function splitIntoChunks(text: string, maxLen: number): string[] {
  const chunks: string[] = [];
  const sentences = text.split(/(?<=[.!?])\s+/);
  let current = "";
  for (const s of sentences) {
    if ((current + " " + s).length > maxLen && current) { chunks.push(current.trim()); current = s; }
    else current = current ? current + " " + s : s;
  }
  if (current.trim()) chunks.push(current.trim());
  return chunks.length > 0 ? chunks : [text.slice(0, maxLen)];
}
